import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  ChevronDown,
  Pencil,
  Trash2,
  MoreVertical,
  Tent,
  Star,
  Search,
  Copy,
  Layers,
  Download,
  Upload,
  XCircle,
  UserPlus,
  MapPin,
  CalendarClock,
  CalendarDays,
  List,
} from 'lucide-react';
import {
  Button,
  Badge,
  Drawer,
  Input,
  Field,
  FieldLabel,
  FieldError,
  type PaginationState,
  PaginatedTable,
  type PaginatedTableColumn,
} from '../../../../components/ui';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy, getUserAcademiesFromCache } from '../../../../hooks/useDataStore';
import { useCourts, useCoaches, useLessons, usePrograms, useLocations, useTerms } from '../../../../hooks/useDataStore';
import { useLessonCalendarStore } from '../../../../store/lessonCalendar';
import { AcademySportSelectField } from '@/components/sports/AcademySportSelectField';
import { useAcademySports } from '@/queries/academySports';
import { resolvePrimaryAcademySportId } from '@/utils/sportPresentation';
import { formatSchedulingErrorMessage } from '@/utils/schedulingErrors';
import { invalidateLessonsQueries } from '../../../../queries/lessons';
import {
  Lesson,
  lessonFormSchema,
  getLessonCourtIds,
  getLessonCoachIds,
  computeCampWeekSessionDates,
  TierFeatureKey,
  type LessonCreateRequest,
  type CampSeriesCreateRequest,
  type LessonFormData,
  type AgeGroup,
  hasAcademyManagementRole,
} from '@court-sync/shared';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { lessonService } from '../../../../services/lesson';
import { ageGroupService } from '../../../../services/ageGroup';
import { DeleteCampConfirmDialog } from './DeleteCampConfirmDialog';
import { CampInstanceStudentsDrawer } from './CampInstanceStudentsDrawer';
import CancelTermLessonModal from '@/components/lessons/CancelTermLessonModal';
import { lessonOverrideService } from '@/services/lessonOverride';
import {
  formatDateRange,
  isCampPast,
  parseCampDate,
  campHasEnrollments,
} from './campDisplayUtils';
import { useHasMultiLocation } from '../../../../hooks/useHasMultiLocation';
import { useSubscriptionCheck } from '../../../../hooks/useSubscriptionCheck';
import { PageHeader } from '@/components/layout/PageHeader';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { getCurrencyFromRegion, getCurrencySymbol, formatCurrency, type Currency } from '../../../../utils/currency';
import { ImportModal, type ImportRow, type ImportResult, type ImportContext, type ImportFailure } from '@/components/ImportModal';
import { downloadCSV } from '@/utils/csvUtils';
import { toApiLessonTime } from '@/utils/timeFormat';
import { LessonCoverImageField } from '@/components/lessons/LessonCoverImageField';
import { uploadLessonImage } from '@/services/storage';
import { useCoverImageUpload } from '@/hooks/useCoverImageUpload';
import { useProgramLessonTemplate } from '@/hooks/useProgramLessonTemplate';
import { CAMP_CREATE_TOUR_STEP_IDS, CAMP_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { isPageTourComplete } from '@/lib/tours/pageTourCompletion';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { AcademyScheduleCalendar } from '@/components/schedule/AcademyScheduleCalendar';
import {
  DayCourtScheduleView,
  type DayCourtReschedulePayload,
} from '@/components/schedule/DayCourtScheduleView';
import { SessionRescheduleNotifyDialog } from '@/components/schedule/SessionRescheduleNotifyDialog';
import {
  DayColumnAxisToggle,
  persistDayColumnAxis,
  readStoredDayColumnAxis,
  type DayScheduleColumnAxis,
} from '@/components/schedule/DayColumnAxisToggle';
import { commitDayScheduleDrag } from '@/utils/dayScheduleMutations';
import {
  buildAcademyOccurrencesFromLessonSessions,
  scheduleOccurrenceToClickPayload,
  type LessonOccurrenceClickPayload,
} from '@/utils/scheduleOccurrences';
import { LessonSessionDetailModal } from '@/components/lessons/LessonSessionDetailModal';
import { isSameDay, startOfWeek, subMonths, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { dateForDayOfWeekInWeek } from '@/utils/scheduleWeek';
import { lessonMatchesScheduleLocationFilter } from '@/utils/lessonLocationFilter';
import { getSessionRescheduleConfirmMeta } from '@/utils/sessionRescheduleUi';
import { CampsGuideOverview } from './CampsGuideInfo';
import {
  CampLessonPricingFields,
  CampLessonScheduleFields,
} from '@/components/lessons/CampLessonFormFields';
import {
  campSessionDatesToExplicitSlots,
  CAMP_DAY_LABELS,
  useCampWeekSchedule,
} from '@/utils/campLessonForm';

const DAY_LABELS = [...CAMP_DAY_LABELS];

function normalizeLessonProgramId(programId: LessonFormData['programId']): string | null {
  return programId && String(programId).trim().length > 0 ? programId : null;
}

function campImportErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { error?: unknown } } }).response?.data;
    const msg = data?.error;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  if (err instanceof Error && err.message) return err.message;
  return '';
}

function formatDays(days: number[] | undefined): string {
  if (!days || days.length === 0) return '—';
  return days.map((d) => DAY_LABELS[d]).join(', ');
}

function formatTime(t: string): string {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${suffix}`;
}

function dateToFormString(value: unknown): string {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString().split('T')[0];
  if (typeof value === 'string') return value.split('T')[0];
  const v = value as { toDate?: () => Date; seconds?: number; _seconds?: number };
  if (v.toDate && typeof v.toDate === 'function') return v.toDate().toISOString().split('T')[0];
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000).toISOString().split('T')[0];
  if (typeof v._seconds === 'number') return new Date(v._seconds * 1000).toISOString().split('T')[0];
  return '';
}

function normalizeTime(t: string | undefined): string {
  if (!t) return '';
  const m = t.match(/^(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : t;
}

type CampListTab = 'current' | 'previous';
type CampScheduleViewMode = 'list' | 'weekly' | 'day';

const CAMP_SCHEDULE_VIEW_STORAGE_KEY = 'courtsync.campManagement.scheduleView';

function readStoredCampScheduleView(): CampScheduleViewMode {
  try {
    const stored = localStorage.getItem(CAMP_SCHEDULE_VIEW_STORAGE_KEY);
    if (stored === 'list' || stored === 'weekly' || stored === 'day') {
      return stored;
    }
  } catch {
    /* localStorage unavailable */
  }
  return 'list';
}

function persistCampScheduleView(view: CampScheduleViewMode): void {
  try {
    localStorage.setItem(CAMP_SCHEDULE_VIEW_STORAGE_KEY, view);
  } catch {
    /* localStorage unavailable */
  }
}

function filterCampsForList(
  list: Lesson[],
  options: {
    campListTab: CampListTab;
    campSearchQuery: string;
    campFeaturedOnly: boolean;
    campLocationFilter?: string;
    courts?: ReadonlyArray<{ id: string; locationId?: string | null }>;
  }
): Lesson[] {
  let filtered = options.campListTab === 'previous' ? list.filter(isCampPast) : list.filter((c) => !isCampPast(c));

  const q = options.campSearchQuery.trim().toLowerCase();
  if (q) {
    filtered = filtered.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const desc = (c.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }
  if (options.campFeaturedOnly) filtered = filtered.filter((c) => c.isFeatured);
  if (options.campLocationFilter && options.courts) {
    filtered = filtered.filter((c) =>
      lessonMatchesScheduleLocationFilter(c, options.campLocationFilter, options.courts!)
    );
  }
  return filtered;
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
function CampManagementContent() {
  const { user } = useAuthStore();
  const { currentAcademy, loadCurrentAcademy, loadUserAcademies } = useCurrentAcademy();
  const navigate = useNavigate();
  const { hasValidSubscription } = useSubscriptionCheck();
  const hasMultiLocation = useHasMultiLocation();
  const { locations } = useLocations(currentAcademy?.id, {
    includeInactive: true,
    enabled: hasMultiLocation,
  });
  const { sports: academySports } = useAcademySports(currentAcademy?.id);
  const defaultAcademySportId = resolvePrimaryAcademySportId(academySports);

  const campLessonFilters = useMemo(() => ({ lessonType: 'camp' as const }), []);

  const { lessons: allLessons, isLoading: lessonsLoading, createLesson, updateLesson, deleteLesson, refresh: refreshLessons } = useLessons(
    currentAcademy?.id,
    campLessonFilters
  );

  const refreshCampsAndCalendar = useCallback(async () => {
    if (!currentAcademy?.id) return;
    await Promise.all([
      refreshLessons(),
      invalidateLessonsQueries(currentAcademy.id),
      useLessonCalendarStore.getState().refreshAfterLessonMutation(currentAcademy.id),
    ]);
  }, [currentAcademy?.id, refreshLessons]);
  const { courts, isLoading: courtsLoading } = useCourts(currentAcademy?.id);
  const { coaches, isLoading: coachesLoading } = useCoaches(currentAcademy?.id);
  const { terms } = useTerms(currentAcademy?.id);
  const lessonSessions = useLessonCalendarStore((state) => state.lessonSessions);
  const overrides = useLessonCalendarStore((state) => state.overrides);
  const makeupSessions = useLessonCalendarStore((state) => state.makeupSessions);
  const schedulePendingBookings = useLessonCalendarStore((state) => state.schedulePendingBookings);
  const scheduleEnrollmentBookings = useLessonCalendarStore(
    (state) => state.scheduleEnrollmentBookings
  );
  const removedFromSessionEnrollments = useLessonCalendarStore(
    (state) => state.removedFromSessionEnrollments
  );
  const reportedAbsenceSessions = useLessonCalendarStore((state) => state.reportedAbsenceSessions);
  const fetchOwnerScheduleForRange = useLessonCalendarStore((state) => state.fetchOwnerScheduleForRange);
  const refreshOwnerScheduleAfterMutation = useLessonCalendarStore(
    (state) => state.refreshOwnerScheduleAfterMutation
  );
  const [campScheduleView, setCampScheduleViewState] = useState<CampScheduleViewMode>(
    readStoredCampScheduleView
  );
  const [dayColumnAxis, setDayColumnAxisState] = useState<DayScheduleColumnAxis>(readStoredDayColumnAxis);
  const setDayColumnAxis = useCallback((axis: DayScheduleColumnAxis) => {
    setDayColumnAxisState(axis);
    persistDayColumnAxis(axis);
  }, []);
  const setCampScheduleView = useCallback((view: CampScheduleViewMode) => {
    setCampScheduleViewState(view);
    persistCampScheduleView(view);
  }, []);
  const [campCalendarWeekStart, setCampCalendarWeekStart] = useState(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [campSelectedDayOfWeek, setCampSelectedDayOfWeek] = useState(() => new Date().getDay());
  const [campSessionDetail, setCampSessionDetail] = useState<LessonOccurrenceClickPayload | null>(null);
  const [pendingCampReschedule, setPendingCampReschedule] = useState<DayCourtReschedulePayload | null>(null);
  const [campRescheduleSaving, setCampRescheduleSaving] = useState(false);
  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { programs } = usePrograms(currentAcademy?.id, { enabled: hasProgramsAccess });

  const camps = useMemo(
    () => allLessons.filter((l) => l.academyId === currentAcademy?.id),
    [allLessons, currentAcademy?.id]
  );
  const [cancelCampTarget, setCancelCampTarget] = useState<Lesson | null>(null);
  const [cancelCampSubmitting, setCancelCampSubmitting] = useState(false);
  const [deletingCamp, setDeletingCamp] = useState<Lesson | null>(null);
  const [deletingCampLoading, setDeletingCampLoading] = useState(false);
  const [staffEnrollCamp, setStaffEnrollCamp] = useState<Lesson | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const [campListTab, setCampListTab] = useState<CampListTab>('current');
  const [campSearchQuery, setCampSearchQuery] = useState('');
  const [campFeaturedOnly, setCampFeaturedOnly] = useState(false);
  const [campLocationFilter, setCampLocationFilter] = useState('');
  const [campPage, setCampPage] = useState(1);
  const campsPerPage = 10;

  const activeLocations = useMemo(
    () => locations.filter((l) => l.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [locations]
  );

  const campLocationFilterLabel = useMemo(() => {
    if (!campLocationFilter) return 'All locations';
    return activeLocations.find((loc) => loc.id === campLocationFilter)?.name ?? 'Location';
  }, [campLocationFilter, activeLocations]);

  const hasActiveCampFilters =
    campSearchQuery.trim().length > 0 || campFeaturedOnly || campLocationFilter.length > 0;

  const clearCampFilters = useCallback(() => {
    setCampSearchQuery('');
    setCampFeaturedOnly(false);
    setCampLocationFilter('');
  }, []);

  const listFilterOptions = useMemo(
    () => ({
      campListTab,
      campSearchQuery,
      campFeaturedOnly,
      campLocationFilter: campLocationFilter || undefined,
      courts,
    }),
    [campListTab, campSearchQuery, campFeaturedOnly, campLocationFilter, courts]
  );

  const campsForTab = useMemo(() => {
    if (campListTab === 'previous') return camps.filter(isCampPast);
    return camps.filter((c) => !isCampPast(c));
  }, [camps, campListTab]);

  const filteredCamps = useMemo(
    () => filterCampsForList(camps, listFilterOptions),
    [camps, listFilterOptions]
  );

  const currentCampsCount = useMemo(() => camps.filter((c) => !isCampPast(c)).length, [camps]);
  const previousCampsCount = useMemo(() => camps.filter(isCampPast).length, [camps]);

  const sortedFilteredCamps = useMemo(() => {
    const copy = [...filteredCamps];
    copy.sort((a, b) => {
      const endA = parseCampDate((a as { endDate?: unknown }).endDate);
      const endB = parseCampDate((b as { endDate?: unknown }).endDate);
      const startA = parseCampDate((a as { startDate?: unknown }).startDate);
      const startB = parseCampDate((b as { startDate?: unknown }).startDate);
      if (campListTab === 'previous') {
        return (endB?.getTime() ?? 0) - (endA?.getTime() ?? 0);
      }
      return (startA?.getTime() ?? 0) - (startB?.getTime() ?? 0);
    });
    return copy;
  }, [filteredCamps, campListTab]);

  const campTotalPages = Math.max(1, Math.ceil(sortedFilteredCamps.length / campsPerPage));
  const paginatedCamps = useMemo(() => {
    const start = (campPage - 1) * campsPerPage;
    return sortedFilteredCamps.slice(start, start + campsPerPage);
  }, [sortedFilteredCamps, campPage, campsPerPage]);

  const courtNamesById = useMemo(
    () => Object.fromEntries(courts.map((c) => [c.id, c.name] as const)),
    [courts]
  );

  const activeCourts = useMemo(() => {
    let list = courts.filter((c) => c.isActive !== false);
    if (hasMultiLocation && campLocationFilter) {
      list = list.filter((c) => c.locationId === campLocationFilter);
    }
    return list;
  }, [courts, hasMultiLocation, campLocationFilter]);

  const campScheduleLessons = useMemo(() => sortedFilteredCamps, [sortedFilteredCamps]);

  const campPlanningDate = useMemo(
    () => dateForDayOfWeekInWeek(campCalendarWeekStart, campSelectedDayOfWeek),
    [campCalendarWeekStart, campSelectedDayOfWeek]
  );

  const campDayOccurrences = useMemo(() => {
    const all = buildAcademyOccurrencesFromLessonSessions(
      campScheduleLessons,
      terms,
      lessonSessions,
      makeupSessions,
      schedulePendingBookings,
      reportedAbsenceSessions,
      courtNamesById,
      undefined,
      'active',
      scheduleEnrollmentBookings,
      removedFromSessionEnrollments
    );
    return all.filter((occ) => isSameDay(occ.date, campPlanningDate));
  }, [
    campScheduleLessons,
    terms,
    lessonSessions,
    makeupSessions,
    schedulePendingBookings,
    reportedAbsenceSessions,
    courtNamesById,
    scheduleEnrollmentBookings,
    removedFromSessionEnrollments,
    campPlanningDate,
  ]);

  useEffect(() => {
    if (!currentAcademy?.id || campScheduleView === 'list') return;
    const start = startOfMonth(subMonths(new Date(), 1));
    const end = endOfMonth(addMonths(new Date(), 3));
    void fetchOwnerScheduleForRange(currentAcademy.id, start, end);
  }, [currentAcademy?.id, campScheduleView, fetchOwnerScheduleForRange]);

  const commitCampReschedule = useCallback(
    async (payload: DayCourtReschedulePayload, notifyGuardians: boolean, reason?: string) => {
      if (!currentAcademy?.id || !payload.occurrence.sessionId) {
        toast.error('This session cannot be rescheduled yet');
        return;
      }
      setCampRescheduleSaving(true);
      try {
        await commitDayScheduleDrag({
          payload,
          academyId: currentAcademy.id,
          notifyGuardians,
          reason,
        });
        await refreshOwnerScheduleAfterMutation(currentAcademy.id);
        toast.success('Camp session updated');
        setPendingCampReschedule(null);
      } catch (err) {
        toast.error(formatSchedulingErrorMessage(err, 'Failed to update session'));
      } finally {
        setCampRescheduleSaving(false);
      }
    },
    [currentAcademy?.id, refreshOwnerScheduleAfterMutation]
  );

  const handleCampOccurrenceReschedule = useCallback(
    (payload: DayCourtReschedulePayload) => {
      const { needsConfirmDialog } = getSessionRescheduleConfirmMeta(payload, currentAcademy);
      if (needsConfirmDialog) {
        setPendingCampReschedule(payload);
        return;
      }
      void commitCampReschedule(payload, false);
    },
    [commitCampReschedule, currentAcademy]
  );

  const pendingCampRescheduleMeta = pendingCampReschedule
    ? getSessionRescheduleConfirmMeta(pendingCampReschedule, currentAcademy)
    : null;

  useEffect(() => {
    setCampPage(1);
  }, [campListTab, campSearchQuery, campFeaturedOnly, campLocationFilter]);

  useEffect(() => {
    if (campPage > campTotalPages) setCampPage(campTotalPages);
  }, [campPage, campTotalPages]);

  const loading = lessonsLoading || courtsLoading || coachesLoading;
  const { startPageTour } = usePageTourActions();
  const campCreateTourScheduledRef = useRef(false);
  const cancelCampCreateTourScheduleRef = useRef<(() => void) | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const CAMP_FORM_ID = 'camp-form';
  const [editingCamp, setEditingCamp] = useState<Lesson | null>(null);
  const [weekCount, setWeekCount] = useState(1);
  const [academyAgeGroups, setAcademyAgeGroups] = useState<AgeGroup[]>([]);

  const pageScrollAnchorRef = useRef<HTMLDivElement>(null);

  /** Single scroll container is dashboard main; reset its scroll when opening create/edit so list position does not leave a blank band. */
  useLayoutEffect(() => {
    if (!showForm) return;
    pageScrollAnchorRef.current?.scrollIntoView({ block: 'start' });
  }, [showForm]);

  const academyCurrency: Currency = currentAcademy?.currency || getCurrencyFromRegion(currentAcademy?.region);
  const currencySymbol = getCurrencySymbol(academyCurrency);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<LessonFormData>({
    resolver: zodResolver(lessonFormSchema),
    defaultValues: {
      lessonType: 'camp',
      daysOfWeek: [1, 2, 3, 4, 5],
      maxCapacity: 12,
      price: 200,
      campDayPrice: 45,
      campFullPrice: 200,
      isActive: true,
      isFeatured: false,
      termIds: [],
      startDate: '',
      endDate: '',
      campSessionDates: [],
      courtIds: [],
      coachIds: [],
      skillLevel: 'any',
      ageGroup: 'all',
      ageGroupId: '',
      programId: null,
      academySportId: undefined,
      locationId: '',
      imageUrl: '',
    },
  });

  const selectedCourtIds = watch('courtIds') || [];
  const selectedCoachIds = watch('coachIds') || [];
  const watchedMaxCapacity = watch('maxCapacity') ?? 0;
  const watchedLocationId = watch('locationId') || '';
  const watchedCampFull = watch('campFullPrice');
  const watchedImageUrl = watch('imageUrl') || '';

  const {
    displayUrl: coverDisplayUrl,
    uploading: uploadingCover,
    handleFileSelect: handleCoverFile,
    handleRemove: handleCoverRemove,
    clear: clearCoverUi,
    resolveImagePayloadForSubmit: resolveCoverImagePayload,
  } = useCoverImageUpload({
    academyId: currentAcademy?.id,
    entityId: editingCamp?.id,
    imageUrl: watchedImageUrl,
    setImageUrl: (url) => setValue('imageUrl', url, { shouldDirty: true }),
    upload: uploadLessonImage,
  });

  const { applyProgramTemplate, helperText: programTemplateHelperText } = useProgramLessonTemplate({
    programs,
    setValue,
    clearCoverUi,
  });

  const watchedStartDate = watch('startDate') || '';
  const selectedDaysOfWeek = watch('daysOfWeek') || [1, 2, 3, 4, 5];
  const watchedProgramId = watch('programId');
  const watchedAcademySportId = watch('academySportId');
  const watchedCampName = watch('name') || '';

  const programsForCampForm = useMemo(() => {
    const sportId = watchedAcademySportId || defaultAcademySportId;
    const selectedId = watchedProgramId;
    if (!sportId) return programs;
    return programs.filter(
      (p) => !p.academySportId || p.academySportId === sportId || p.id === selectedId
    );
  }, [programs, watchedAcademySportId, defaultAcademySportId, watchedProgramId]);

  const computedWeekDates = useCampWeekSchedule(
    showForm,
    watchedStartDate,
    selectedDaysOfWeek,
    setValue
  );

  const toggleDaySelection = useCallback(
    (dayIndex: number) => {
      const current = getValues('daysOfWeek') || [];
      const next = current.includes(dayIndex)
        ? current.filter((d) => d !== dayIndex).sort((a, b) => a - b)
        : [...current, dayIndex].sort((a, b) => a - b);
      setValue('daysOfWeek', next, { shouldValidate: true, shouldDirty: true });
    },
    [getValues, setValue]
  );

  useEffect(() => {
    if (watchedCampFull != null && !Number.isNaN(watchedCampFull)) {
      setValue('price', watchedCampFull, { shouldValidate: true });
    }
  }, [watchedCampFull, setValue]);

  const activeCourtsForPicker = useMemo(() => {
    const active = courts.filter((c) => c.isActive);
    if (!hasMultiLocation || locations.length === 0 || !watchedLocationId) {
      return active;
    }
    return active.filter((c) => c.locationId === watchedLocationId);
  }, [courts, hasMultiLocation, locations.length, watchedLocationId]);

  useEffect(() => {
    if (!showForm || !hasMultiLocation || locations.length === 0) return;
    const ids = getValues('courtIds') || [];
    const allowed = new Set(activeCourtsForPicker.map((c) => c.id));
    const next = ids.filter((id) => allowed.has(id));
    if (next.length !== ids.length) {
      setValue('courtIds', next, { shouldValidate: true });
    }
  }, [watchedLocationId, showForm, hasMultiLocation, locations.length, activeCourtsForPicker, setValue, getValues]);

  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user || !currentAcademy) return;
    const refreshAcademyData = async () => {
      try {
        await loadUserAcademies({ force: true });
        const userAcademies = getUserAcademiesFromCache();
        const storeAcademy = currentAcademy;
        if (storeAcademy?.id) {
          await loadCurrentAcademy(storeAcademy.id);
        } else if (userAcademies.length > 0) {
          await loadCurrentAcademy(userAcademies[0].id);
        }
      } catch {
        // non-fatal
      }
    };
    refreshAcademyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  useEffect(() => {
    if (!currentAcademy?.id) return;
    ageGroupService.getAgeGroups(currentAcademy.id).then((groups) => {
      setAcademyAgeGroups(groups.filter((g) => g.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
    }).catch(() => setAcademyAgeGroups([]));
  }, [currentAcademy?.id]);

  const toggleCourtSelection = useCallback((courtId: string) => {
    const current = selectedCourtIds;
    setValue('courtIds', current.includes(courtId) ? current.filter((id) => id !== courtId) : [...current, courtId]);
  }, [selectedCourtIds, setValue]);

  const toggleCoachSelection = useCallback((coachId: string) => {
    const current = selectedCoachIds;
    setValue('coachIds', current.includes(coachId) ? current.filter((id) => id !== coachId) : [...current, coachId]);
  }, [selectedCoachIds, setValue]);

  const onSubmit = async (data: LessonFormData) => {
    if (!currentAcademy) {
      toast.error('No academy selected');
      return;
    }
    const startTime = new Date(`2000-01-01 ${data.startTime}`);
    const endTime = new Date(`2000-01-01 ${data.endTime}`);
    if (endTime <= startTime) {
      toast.error('End time must be after start time');
      return;
    }

    try {
      setSubmitting(true);
      let refreshCalendarAfterSave = false;

      const locationPayload = hasMultiLocation
        ? { locationId: data.locationId && data.locationId.length > 0 ? data.locationId : null }
        : {};

      const campSessionDates = computeCampWeekSessionDates(data.startDate ?? '', data.daysOfWeek ?? []);
      if (campSessionDates.length === 0) {
        toast.error('Select a start date and at least one day of the week');
        return;
      }

      const endDate = campSessionDates[campSessionDates.length - 1]!;

      const full = data.campFullPrice ?? data.price;
      const imagePayload = resolveCoverImagePayload();
      const explicitCampSessions = campSessionDatesToExplicitSlots(
        campSessionDates,
        data.startTime!,
        data.endTime!
      );
      const payload: Record<string, unknown> = {
        academyId: currentAcademy.id,
        courtIds: data.courtIds!,
        coachIds: data.coachIds!,
        name: data.name!,
        description: data.description || undefined,
        lessonType: 'camp' as const,
        skillLevel: data.skillLevel || undefined,
        ageGroup: data.ageGroup || undefined,
        ageGroupId: data.ageGroupId || undefined,
        daysOfWeek: data.daysOfWeek!,
        startTime: data.startTime!,
        endTime: data.endTime!,
        maxCapacity: data.maxCapacity!,
        price: full,
        campDayPrice: data.campDayPrice!,
        campFullPrice: data.campFullPrice!,
        currency: academyCurrency,
        isActive: data.isActive!,
        isFeatured: data.isFeatured || false,
        startDate: data.startDate,
        endDate,
        campSessionDates,
        explicitCampSessions,
        programId: normalizeLessonProgramId(data.programId),
        academySportId: data.academySportId || defaultAcademySportId,
        ...locationPayload,
        ...imagePayload,
      };

      if (editingCamp) {
        await updateLesson(editingCamp.id, payload);
        toast.success('Camp updated successfully');
      } else {
        const savedImageUrl =
          typeof imagePayload.imageUrl === 'string' ? imagePayload.imageUrl : undefined;
        const { imageUrl: _i, ...createBody } = payload;
        const createPayload = createBody as LessonCreateRequest;
        if (weekCount > 1) {
          const seriesPayload: CampSeriesCreateRequest = {
            ...createPayload,
            weeks: weekCount,
          };
          const response = await lessonService.createCampSeries(seriesPayload);
          refreshCalendarAfterSave = true;
          const ids = response.data?.ids ?? [];
          if (ids.length > 0 && savedImageUrl) {
            await updateLesson(ids[0]!, { imageUrl: savedImageUrl });
          }
          toast.success(
            ids.length === 1 ? 'Camp created successfully' : `${ids.length} camp weeks created successfully`
          );
        } else {
          await createLesson({
            ...createPayload,
            ...(savedImageUrl ? { imageUrl: savedImageUrl } : {}),
          } as LessonCreateRequest);
          toast.success('Camp created successfully');
        }
      }

      handleCancelEdit();
      if (currentAcademy?.id) {
        await refreshLessons();
        if (refreshCalendarAfterSave) {
          await useLessonCalendarStore.getState().refreshAfterLessonMutation(currentAcademy.id);
        }
      }
    } catch (error: unknown) {
      toast.error(formatSchedulingErrorMessage(error, 'Failed to save camp'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (camp: Lesson) => {
    clearCoverUi();
    setEditingCamp(camp);
    setWeekCount(1);
    setShowForm(true);

    const legacyDayOfWeek = (camp as Lesson & { dayOfWeek?: number }).dayOfWeek;
    const rawDays =
      camp.daysOfWeek ?? (legacyDayOfWeek !== undefined ? [legacyDayOfWeek] : [1, 2, 3, 4, 5]);
    const daysOfWeek = Array.isArray(rawDays)
      ? rawDays.map((d: number | string) => (typeof d === 'string' ? parseInt(d, 10) : d)).filter((n: number) => !Number.isNaN(n) && n >= 0 && n <= 6)
      : [1, 2, 3, 4, 5];

    const startDateStr = dateToFormString((camp as { startDate?: unknown }).startDate);
    const campSessionDates = computeCampWeekSessionDates(startDateStr, daysOfWeek);

    reset({
      lessonType: 'camp',
      termIds: [],
      startDate: startDateStr,
      endDate: campSessionDates.length > 0 ? campSessionDates[campSessionDates.length - 1]! : dateToFormString((camp as { endDate?: unknown }).endDate),
      campSessionDates,
      courtIds: getLessonCourtIds(camp),
      coachIds: getLessonCoachIds(camp),
      name: camp.name || '',
      description: camp.description || '',
      skillLevel: camp.skillLevel || 'any',
      ageGroup: camp.ageGroup || 'all',
      ageGroupId: camp.ageGroupId || undefined,
      programId: camp.programId ?? null,
      academySportId: camp.academySportId ?? defaultAcademySportId,
      daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : [1],
      startTime: normalizeTime(camp.startTime),
      endTime: normalizeTime(camp.endTime),
      maxCapacity: camp.maxCapacity,
      price: camp.campFullPrice ?? camp.price,
      campDayPrice: camp.campDayPrice ?? camp.price,
      campFullPrice: camp.campFullPrice ?? camp.price,
      isActive: camp.isActive,
      isFeatured: camp.isFeatured || false,
      locationId: camp.locationId || '',
      imageUrl: camp.imageUrl || '',
    });
  };

  const handleDuplicate = (camp: Lesson) => {
    clearCoverUi();
    setEditingCamp(null);
    setWeekCount(1);
    setShowForm(true);

    const copyName = `${camp.name || 'Camp'} (copy)`;

    const legacyDayOfWeek = (camp as Lesson & { dayOfWeek?: number }).dayOfWeek;
    const rawDays =
      camp.daysOfWeek ?? (legacyDayOfWeek !== undefined ? [legacyDayOfWeek] : [1, 2, 3, 4, 5]);
    const daysOfWeek = Array.isArray(rawDays)
      ? rawDays.map((d: number | string) => (typeof d === 'string' ? parseInt(d, 10) : d)).filter((n: number) => !Number.isNaN(n) && n >= 0 && n <= 6)
      : [1, 2, 3, 4, 5];

    reset({
      lessonType: 'camp',
      termIds: [],
      startDate: '',
      endDate: '',
      campSessionDates: [],
      courtIds: getLessonCourtIds(camp),
      coachIds: getLessonCoachIds(camp),
      name: copyName,
      description: camp.description || '',
      skillLevel: camp.skillLevel || 'any',
      ageGroup: camp.ageGroup || 'all',
      ageGroupId: camp.ageGroupId || undefined,
      programId: camp.programId ?? null,
      academySportId: camp.academySportId ?? defaultAcademySportId,
      daysOfWeek: daysOfWeek.length > 0 ? daysOfWeek : [1, 2, 3, 4, 5],
      startTime: normalizeTime(camp.startTime),
      endTime: normalizeTime(camp.endTime),
      maxCapacity: camp.maxCapacity,
      price: camp.campFullPrice ?? camp.price,
      campDayPrice: camp.campDayPrice ?? camp.price,
      campFullPrice: camp.campFullPrice ?? camp.price,
      isActive: camp.isActive,
      isFeatured: camp.isFeatured || false,
      locationId: camp.locationId || '',
      imageUrl: camp.imageUrl || '',
    });
  };

  const handleDeleteRequest = (camp: Lesson) => {
    if (campHasEnrollments(camp)) {
      toast.error(
        'This camp has enrolled students. Cancel the camp or remove all students before deleting.'
      );
      return;
    }
    setDeletingCamp(camp);
  };

  const confirmDeleteCamp = async () => {
    if (!deletingCamp) return;
    setDeletingCampLoading(true);
    try {
      await deleteLesson(deletingCamp.id);
      toast.success('Camp deleted successfully');
      setDeletingCamp(null);
      await refreshCampsAndCalendar();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete camp');
    } finally {
      setDeletingCampLoading(false);
    }
  };

  const handleCancelCampRequest = useCallback((camp: Lesson) => {
    if (camp.cancelledFromDate) return;
    setCancelCampTarget(camp);
  }, []);

  const handleCancelCampConfirm = async (
    reason: string,
    notifyStudents: boolean,
    grantMakeupCredit: boolean
  ) => {
    if (!cancelCampTarget || !currentAcademy?.id) return;
    setCancelCampSubmitting(true);
    try {
      await lessonOverrideService.cancelTermLesson(
        cancelCampTarget.id,
        currentAcademy.id,
        reason,
        undefined,
        notifyStudents,
        grantMakeupCredit
      );
      toast.success('Camp cancelled.');
      setCancelCampTarget(null);
      await refreshCampsAndCalendar();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel camp');
    } finally {
      setCancelCampSubmitting(false);
    }
  };

  const handleExportCamps = () => {
    const exportData = camps.map((camp) => {
      const gid = camp.ageGroupId;
      const customLabel = gid
        ? academyAgeGroups.find((g) => g.id === gid)?.label ?? ''
        : '';
      return {
        name: camp.name,
        lessonType: 'camp',
        startDate: dateToFormString((camp as { startDate?: unknown }).startDate),
        endDate: dateToFormString((camp as { endDate?: unknown }).endDate),
        daysOfWeek: (camp.daysOfWeek || []).join(','),
        startTime: toApiLessonTime(camp.startTime),
        endTime: toApiLessonTime(camp.endTime),
        maxCapacity: camp.maxCapacity,
        campDayPrice: camp.campDayPrice ?? camp.price,
        campFullPrice: camp.campFullPrice ?? camp.price,
        skillLevel: camp.skillLevel || 'any',
        ageGroup: camp.ageGroup || 'all',
        ageGroupId: gid ?? '',
        ageGroupLabel: customLabel,
        isFeatured: camp.isFeatured ? 'true' : 'false',
        isActive: camp.isActive !== false ? 'true' : 'false',
        programName: camp.programId
          ? programs.find((p) => p.id === camp.programId)?.name ?? ''
          : '',
        locationName: camp.locationId
          ? locations.find((l) => l.id === camp.locationId)?.name ?? ''
          : '',
        description: camp.description ?? '',
        courtNames: getLessonCourtIds(camp)
          .map((id) => courts.find((c) => c.id === id)?.name || '')
          .join('; '),
        coachNames: getLessonCoachIds(camp)
          .map((id) => {
            const coach = coaches.find((c) => c.id === id);
            return coach ? `${coach.firstName} ${coach.lastName}` : '';
          })
          .join('; '),
      };
    });
    const slug = (currentAcademy?.name ?? 'academy').replace(/[^\w-]+/g, '-').toLowerCase();
    downloadCSV(`camps-${slug}-${new Date().toISOString().split('T')[0]}.csv`, exportData);
    toast.success(`Exported ${camps.length} camp${camps.length !== 1 ? 's' : ''}`);
  };

  const handleImportCamps = async (
    rows: ImportRow[],
    ctx?: ImportContext
  ): Promise<ImportResult | undefined> => {
    if (!currentAcademy?.id) {
      toast.error('Academy context is not ready. Refresh the page and try again.');
      return undefined;
    }

    let createdCount = 0;
    let failedCount = 0;
    let firstError = '';
    const failures: ImportFailure[] = [];
    const totalRows = rows.length;

    const rowLabel = (row: ImportRow) => {
      const name = (row.data.name || '').trim();
      if (name) return name;
      return undefined;
    };

    const recordFailure = (row: ImportRow, error: string) => {
      failedCount++;
      failures.push({
        rowNumber: row.rowNumber,
        error,
        label: rowLabel(row),
      });
      if (!firstError) firstError = error;
    };

    const reportProgress = (index: number) => {
      ctx?.reportProgress(index, totalRows);
    };

    reportProgress(0);

    const findCourtIdByName = (name: string): string | undefined => {
      const n = name.trim().toLowerCase();
      return courts.find((c) => c.name.trim().toLowerCase() === n)?.id;
    };

    const findCoachIdByDisplayName = (raw: string): string | undefined => {
      const normalized = raw.trim().toLowerCase().replace(/\s+/g, ' ');
      if (!normalized) return undefined;
      return coaches.find((c) => {
        const display = `${c.firstName} ${c.lastName}`.trim().toLowerCase().replace(/\s+/g, ' ');
        return display === normalized;
      })?.id;
    };

    const findProgramIdByName = (name: string): string | null => {
      const n = name.trim().toLowerCase();
      if (!n) return null;
      return programs.find((p) => p.name.trim().toLowerCase() === n)?.id ?? null;
    };

    const findLocationIdByName = (name: string): string | null => {
      const n = name.trim().toLowerCase();
      if (!n) return null;
      return locations.find((l) => l.name.trim().toLowerCase() === n)?.id ?? null;
    };

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      const row = rows[rowIndex]!;
      reportProgress(rowIndex);

      if (!row.isValid) {
        recordFailure(row, row.error || 'Row failed validation');
        continue;
      }

      const rawType = (row.data.lessonType || 'camp').trim().toLowerCase();
      if (rawType !== 'camp') {
        recordFailure(row, 'Only camp rows can be imported on this page');
        continue;
      }

      try {
        const daysOfWeek = (row.data.daysOfWeek || '')
          .split(',')
          .map((d) => parseInt(d.trim(), 10))
          .filter((d) => !isNaN(d) && d >= 0 && d <= 6);

        if (daysOfWeek.length === 0) {
          recordFailure(row, 'daysOfWeek must include at least one valid weekday (0–6)');
          continue;
        }

        const startDate = (row.data.startDate || '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
          recordFailure(row, 'startDate must be YYYY-MM-DD');
          continue;
        }

        const courtNames = (row.data.courtNames || '')
          .split(';')
          .map((n) => n.trim())
          .filter((n) => n.length > 0);
        const courtIds = courtNames
          .map((name) => findCourtIdByName(name))
          .filter((id): id is string => !!id);

        if (courtIds.length === 0) {
          recordFailure(
            row,
            courtNames.length === 0
              ? 'courtNames is required'
              : `No matching courts found for: ${courtNames.join('; ')}`
          );
          continue;
        }

        const coachNames = (row.data.coachNames || '')
          .split(';')
          .map((n) => n.trim())
          .filter((n) => n.length > 0);
        const coachIds = coachNames
          .map((name) => findCoachIdByDisplayName(name))
          .filter((id): id is string => !!id);

        if (coachIds.length === 0) {
          recordFailure(
            row,
            coachNames.length === 0
              ? 'coachNames is required'
              : `No matching coaches found for: ${coachNames.join('; ')}`
          );
          continue;
        }

        const campDayPrice = parseFloat(row.data.campDayPrice || '');
        const campFullPrice = parseFloat(row.data.campFullPrice || '');
        if (Number.isNaN(campDayPrice) || Number.isNaN(campFullPrice)) {
          recordFailure(row, 'campDayPrice and campFullPrice must be numbers');
          continue;
        }

        const campSessionDates = computeCampWeekSessionDates(startDate, daysOfWeek);
        if (campSessionDates.length === 0) {
          recordFailure(row, 'No camp session dates could be computed from startDate and daysOfWeek');
          continue;
        }

        const endDate = campSessionDates[campSessionDates.length - 1]!;
        const startTime = toApiLessonTime(row.data.startTime);
        const endTime = toApiLessonTime(row.data.endTime);
        const explicitCampSessions = campSessionDatesToExplicitSlots(
          campSessionDates,
          startTime,
          endTime
        );

        const uuidRaw = (row.data.ageGroupId || '').trim();
        const ageGroupRaw = (row.data.ageGroup || '').trim();

        let resolvedAgeGroupId: string | undefined;
        let legacyAgeGroup: 'juniors' | 'adults' | 'all' = 'all';

        if (uuidRaw) {
          const foundById = academyAgeGroups.find((g) => g.id === uuidRaw);
          if (foundById) {
            resolvedAgeGroupId = foundById.id;
            legacyAgeGroup = 'all';
          } else {
            recordFailure(row, `Unknown ageGroupId "${uuidRaw}"`);
            continue;
          }
        } else if (ageGroupRaw) {
          const foundByLabel = academyAgeGroups.find(
            (g) => g.label.trim().toLowerCase() === ageGroupRaw.toLowerCase()
          );
          if (foundByLabel) {
            resolvedAgeGroupId = foundByLabel.id;
            legacyAgeGroup = 'all';
          } else if (['juniors', 'adults', 'all'].includes(ageGroupRaw.toLowerCase())) {
            legacyAgeGroup = ageGroupRaw.toLowerCase() as 'juniors' | 'adults' | 'all';
          } else {
            recordFailure(
              row,
              `ageGroup "${ageGroupRaw}" is not a known label or juniors/adults/all`
            );
            continue;
          }
        }

        const programName = (row.data.programName || '').trim();
        const programId = programName ? findProgramIdByName(programName) : null;
        if (programName && !programId) {
          recordFailure(row, `Unknown program "${programName}"`);
          continue;
        }

        const locationName = (row.data.locationName || '').trim();
        const locationId = locationName ? findLocationIdByName(locationName) : null;
        if (locationName && !locationId) {
          recordFailure(row, `Unknown location "${locationName}"`);
          continue;
        }

        const resolvedName = (row.data.name || '').trim();
        if (!resolvedName) {
          recordFailure(row, 'name is required');
          continue;
        }

        const payload = {
          academyId: currentAcademy.id,
          courtIds,
          coachIds,
          name: resolvedName,
          description: (row.data.description || '').trim() || undefined,
          lessonType: 'camp' as const,
          daysOfWeek,
          startTime,
          endTime,
          maxCapacity: parseInt(row.data.maxCapacity || '-1', 10),
          price: campFullPrice,
          campDayPrice,
          campFullPrice,
          currency: academyCurrency,
          skillLevel: (row.data.skillLevel || 'any') as LessonFormData['skillLevel'],
          ageGroup: legacyAgeGroup,
          ...(resolvedAgeGroupId ? { ageGroupId: resolvedAgeGroupId } : {}),
          isActive: row.data.isActive !== 'false',
          isFeatured: row.data.isFeatured === 'true',
          startDate,
          endDate,
          campSessionDates,
          explicitCampSessions,
          programId,
          ...(locationId ? { locationId } : {}),
        };

        await createLesson(payload as LessonCreateRequest);
        createdCount++;
      } catch (error: unknown) {
        console.error(`Failed to create camp at row ${row.rowNumber}:`, error);
        recordFailure(row, campImportErrorMessage(error) || 'Failed to create camp');
      }
    }

    reportProgress(totalRows);

    if (createdCount === 0 && failedCount > 0) {
      toast.error(
        firstError
          ? `${firstError} (${failedCount} row${failedCount !== 1 ? 's' : ''}).`
          : `No camps were created (${failedCount} row${failedCount !== 1 ? 's' : ''} failed). Check court and coach names, dates, and pricing.`
      );
    } else if (createdCount > 0) {
      toast.success(
        `Created ${createdCount} camp${createdCount !== 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} failed` : ''}`
      );
    }
    await refreshLessons();
    return { created: createdCount, failed: failedCount, failures };
  };

  const campsFormatGuide = (
    <div className="space-y-3 text-xs">
      <div>
        <p className="font-semibold mb-2">Required columns</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">name</code> — Camp name</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">lessonType</code> — Must be &quot;camp&quot;</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">startDate</code> — YYYY-MM-DD (first day of the camp week)</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">daysOfWeek</code> — Comma-separated (0=Sun, 1=Mon, …, 6=Sat)</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">startTime</code> — HH:MM or HH:MM:SS</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">endTime</code> — Same as startTime</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">maxCapacity</code> — Number (-1 = no limit)</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">campDayPrice</code> — Per-day price</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">campFullPrice</code> — Full camp package price</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">courtNames</code> — Semicolon-separated court names (must exist)</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">coachNames</code> — Semicolon-separated coach names (must exist)</li>
        </ul>
      </div>
      <div>
        <p className="font-semibold mb-2">Optional columns</p>
        <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">description</code> — Camp description</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">skillLevel</code> — any, beginner, intermediate, advanced</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ageGroupId</code> — UUID of an active custom age group</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">ageGroup</code> — juniors / adults / all, or a custom age group label</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">isFeatured</code> — true or false</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">isActive</code> — true or false (default true)</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">programName</code> — Development program name (Professional+)</li>
          <li><code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">locationName</code> — Academy location name</li>
        </ul>
      </div>
      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-gray-700 dark:text-gray-300">
        <strong>Example:</strong>
        <br />
        name,lessonType,startDate,daysOfWeek,startTime,endTime,maxCapacity,campDayPrice,campFullPrice,skillLevel,ageGroup,isFeatured,isActive,courtNames,coachNames
        <br />
        Summer Week 1,camp,2026-07-06,&quot;1,2,3,4,5&quot;,09:00,15:00,20,45,200,beginner,juniors,true,true,&quot;Court 1&quot;,&quot;Coach Smith&quot;
      </div>
    </div>
  );

  const handleDelete = handleDeleteRequest;

  const handleCancelEdit = () => {
    clearCoverUi();
    setEditingCamp(null);
    setWeekCount(1);
    setShowForm(false);
    reset({
      lessonType: 'camp',
      daysOfWeek: [1, 2, 3, 4, 5],
      maxCapacity: 12,
      price: 200,
      campDayPrice: 45,
      campFullPrice: 200,
      isActive: true,
      isFeatured: false,
      termIds: [],
      startDate: '',
      endDate: '',
      campSessionDates: [],
      courtIds: [],
      coachIds: [],
      name: '',
      description: '',
      skillLevel: 'any',
      ageGroup: 'all',
      programId: null,
      startTime: '',
      endTime: '',
      locationId: '',
      imageUrl: '',
    });
  };

  const defaultNewCampFormValues = useCallback(
    (): LessonFormData => ({
      lessonType: 'camp',
      daysOfWeek: [1, 2, 3, 4, 5],
      maxCapacity: 12,
      price: 200,
      campDayPrice: 45,
      campFullPrice: 200,
      isActive: true,
      isFeatured: false,
      termIds: [],
      startDate: '',
      endDate: '',
      campSessionDates: [],
      courtIds: [],
      coachIds: [],
      name: '',
      description: '',
      skillLevel: 'any',
      ageGroup: 'all',
      programId: null,
      academySportId: defaultAcademySportId,
      ageGroupId: '',
      startTime: '',
      endTime: '',
      locationId: '',
      imageUrl: '',
    }),
    [defaultAcademySportId]
  );

  const openNewCampForm = () => {
    clearCoverUi();
    setEditingCamp(null);
    setWeekCount(1);
    reset(defaultNewCampFormValues());
    setShowForm(true);
  };

  usePageTourReady(
    'academy-camps-create',
    showForm && !editingCamp && Boolean(currentAcademy)
  );

  useEffect(() => {
    if (!showForm) {
      campCreateTourScheduledRef.current = false;
      cancelCampCreateTourScheduleRef.current?.();
      cancelCampCreateTourScheduleRef.current = null;
    }
  }, [showForm]);

  const handleCampCreateDrawerPanelOpen = useCallback(() => {
    if (
      editingCamp ||
      campCreateTourScheduledRef.current ||
      isPageTourComplete(user?.preferences, 'academy-camps-create')
    ) {
      return;
    }
    campCreateTourScheduledRef.current = true;
    cancelCampCreateTourScheduleRef.current?.();
    cancelCampCreateTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'academy-camps-create',
      user?.preferences
    );
  }, [editingCamp, startPageTour, user?.preferences]);

  const handleManageStudents = useCallback((camp: Lesson) => {
    setStaffEnrollCamp(camp);
  }, []);

  const closeStaffEnrollDrawer = useCallback(() => {
    setStaffEnrollCamp(null);
  }, []);

  const campTableColumns: PaginatedTableColumn<Lesson>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Camp',
        render: (camp) => (
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">{camp.name}</span>
              {camp.isFeatured && (
                <Star
                  className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-500"
                  aria-label="Featured"
                />
              )}
            </div>
            {camp.description && (
              <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{camp.description}</p>
            )}
          </div>
        ),
      },
      {
        key: 'dates',
        header: 'Dates',
        render: (camp) => (
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {formatDateRange(
              (camp as { startDate?: unknown }).startDate,
              (camp as { endDate?: unknown }).endDate
            )}
          </span>
        ),
      },
      {
        key: 'schedule',
        header: 'Schedule',
        render: (camp) => (
          <span className="text-sm text-muted-foreground">
            {formatDays(camp.daysOfWeek)} · {formatTime(camp.startTime)}–{formatTime(camp.endTime)}
          </span>
        ),
      },
      {
        key: 'enrollment',
        header: 'Enrolled',
        render: (camp) => {
          const enrollment = camp.currentEnrollment ?? 0;
          const capacity = camp.maxCapacity === -1 ? '∞' : String(camp.maxCapacity ?? '—');
          return (
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {enrollment} / {capacity}
            </span>
          );
        },
      },
      {
        key: 'pricing',
        header: 'Pricing',
        render: (camp) => (
          <div className="text-sm text-muted-foreground">
            <div className="whitespace-nowrap">
              {formatCurrency(camp.campDayPrice ?? camp.price, academyCurrency)} / day
            </div>
            <div className="text-xs whitespace-nowrap">
              Full: {formatCurrency(camp.campFullPrice ?? camp.price, academyCurrency)}
            </div>
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (camp) => {
          if (camp.cancelledFromDate) {
            return <Badge variant="secondary">Cancelled</Badge>;
          }
          return camp.isActive ? (
            <Badge variant="success">Active</Badge>
          ) : (
            <Badge variant="secondary">Inactive</Badge>
          );
        },
      },
      {
        key: 'actions',
        header: <span className="sr-only">Actions</span>,
        className: 'text-right w-[1%] whitespace-nowrap',
        render: (camp) => {
          const enrollment = camp.currentEnrollment ?? 0;
          const hasEnrollments = enrollment > 0;
          const isCancelled = !!camp.cancelledFromDate;
          return (
            <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => handleEdit(camp)}
                aria-label="Edit camp"
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </Button>
              {hasValidSubscription && !isCancelled && (
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => handleManageStudents(camp)}
                  aria-label="Manage students"
                >
                  <UserPlus className="h-4 w-4" aria-hidden />
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={() => handleDuplicate(camp)}
                aria-label="Duplicate camp"
              >
                <Copy className="h-4 w-4" aria-hidden />
              </Button>
              {!isCancelled && hasEnrollments ? (
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => handleCancelCampRequest(camp)}
                  className="text-amber-700 hover:text-amber-700 dark:text-amber-300"
                  aria-label="Cancel camp"
                >
                  <XCircle className="h-4 w-4" aria-hidden />
                </Button>
              ) : !hasEnrollments ? (
                <Button
                  type="button"
                  size="icon"
                  variant="outline"
                  onClick={() => handleDelete(camp)}
                  className="text-red-600 hover:text-red-700 dark:text-red-400"
                  aria-label="Delete camp"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [academyCurrency, handleCancelCampRequest, handleDelete, handleManageStudents, hasValidSubscription]
  );

  if (!user || !hasAcademyManagementRole(user)) return null;

  if (!currentAcademy) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div ref={pageScrollAnchorRef} className="contents">
        <PageHeader
          title="Camps"
          subtitle="Holiday camps, summer camps, and intensive sessions"
          help={
            <PageFeatureHelp title="Holiday camps">
              <CampsGuideOverview />
            </PageFeatureHelp>
          }
          helpAnchorId={CAMP_MANAGEMENT_TOUR_STEP_IDS.GUIDE}
          actions={
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  id={CAMP_MANAGEMENT_TOUR_STEP_IDS.NEW_CAMP}
                  type="button"
                  size="sm"
                  aria-label="New camp options"
                >
                  <Plus aria-hidden />
                  New Camp
                  <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <DropdownMenuItem onSelect={() => openNewCampForm()}>
                  <Tent className="h-4 w-4" aria-hidden />
                  Single camp
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => navigate('/admin/camps/new')}>
                  <Layers className="h-4 w-4" aria-hidden />
                  Multiple camps
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          }
        />

        <Drawer
          isOpen={showForm}
          onClose={handleCancelEdit}
          panelId={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.DRAWER : undefined}
          onPanelOpen={!editingCamp ? handleCampCreateDrawerPanelOpen : undefined}
          title={editingCamp ? 'Edit Camp' : 'New Camp'}
          width="3xl"
          headerActions={
            !editingCamp ? (
              <DrawerTourHelp
                tourKey="academy-camps-create"
                suggestion="New to camps? Tour this form before you save."
                buttonLabel="Tour this form"
              />
            ) : undefined
          }
          footer={
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                id={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.SUBMIT : undefined}
                type="submit"
                form={CAMP_FORM_ID}
                isLoading={submitting || uploadingCover}
                disabled={uploadingCover}
              >
                {editingCamp ? 'Update Camp' : 'Create Camp'}
              </Button>
            </div>
          }
        >
                <form id={CAMP_FORM_ID} onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div
                      id={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.DETAILS : undefined}
                      className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                    >
                      <Field data-invalid={!!errors.name?.message}>
                        <FieldLabel htmlFor="name">Camp Name *</FieldLabel>
                        <Input id="name" aria-invalid={!!errors.name?.message} {...register('name')} placeholder="Summer Tennis Camp" />
                        <FieldError>{errors.name?.message}</FieldError>
                      </Field>

                      {hasProgramsAccess ? (
                        <Field className="md:col-span-2" data-invalid={!!errors.programId?.message}>
                          <FieldLabel htmlFor="campProgramId" className="flex items-center gap-2">
                            <Layers className="h-4 w-4 text-muted-foreground" aria-hidden />
                            Development program
                          </FieldLabel>
                          <select
                            id="campProgramId"
                            aria-invalid={!!errors.programId?.message}
                            className="input"
                            value={watchedProgramId ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              applyProgramTemplate(v ? v : null);
                            }}
                          >
                            <option value="">None</option>
                            {[...programsForCampForm]
                              .sort((a, b) => {
                                if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
                                return a.name.localeCompare(b.name);
                              })
                              .map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.name}
                                  {!p.isActive ? ' (inactive)' : ''}
                                </option>
                              ))}
                          </select>
                          <FieldError>{errors.programId?.message}</FieldError>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {programTemplateHelperText}
                          </p>
                        </Field>
                      ) : null}

                      <AcademySportSelectField
                        academyId={currentAcademy?.id}
                        control={control}
                        name="academySportId"
                        required
                        className="md:col-span-2"
                      />

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Skill Level
                        </label>
                        <select {...register('skillLevel')} className="input">
                          <option value="any">Any Level</option>
                          <option value="beginner">Beginner</option>
                          <option value="intermediate">Intermediate</option>
                          <option value="advanced">Advanced</option>
                        </select>
                      </div>
                    </div>

                    <div
                      id={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.SCHEDULE : undefined}
                      className="md:col-span-2"
                    >
                      <CampLessonScheduleFields
                        register={register}
                        errors={errors}
                        selectedDaysOfWeek={selectedDaysOfWeek}
                        onToggleDay={toggleDaySelection}
                        computedWeekDates={computedWeekDates}
                        region={currentAcademy?.region}
                        showRepeatWeekly={!editingCamp}
                        weekCount={weekCount}
                        onWeekCountChange={setWeekCount}
                        campNameForPreview={watchedCampName}
                      />
                    </div>

                    <div
                      id={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.TIMES : undefined}
                      className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                    >
                      <Field data-invalid={!!errors.startTime?.message}>
                        <FieldLabel htmlFor="startTime">Start Time *</FieldLabel>
                        <Input id="startTime" type="time" aria-invalid={!!errors.startTime?.message} {...register('startTime')} />
                        <FieldError>{errors.startTime?.message}</FieldError>
                      </Field>

                      <Field data-invalid={!!errors.endTime?.message}>
                        <FieldLabel htmlFor="endTime">End Time *</FieldLabel>
                        <Input id="endTime" type="time" aria-invalid={!!errors.endTime?.message} {...register('endTime')} />
                        <FieldError>{errors.endTime?.message}</FieldError>
                      </Field>

                      <div className="md:col-span-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Maximum Capacity *
                          </label>
                          <label className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={watchedMaxCapacity === -1}
                              onChange={(e) => {
                                setValue('maxCapacity', e.target.checked ? -1 : 12, { shouldValidate: true });
                              }}
                            />
                            <span className="text-gray-700 dark:text-gray-300">No limit</span>
                          </label>
                        </div>
                        {watchedMaxCapacity !== -1 ? (
                          <input
                            type="number"
                            {...register('maxCapacity', { valueAsNumber: true })}
                            onWheel={(e) => e.currentTarget.blur()}
                            className="input w-full"
                            placeholder="12"
                            min={1}
                          />
                        ) : (
                          <div className="p-2 border rounded bg-gray-50 text-gray-500 text-sm italic dark:bg-gray-800 dark:border-gray-700">
                            Unlimited students allowed
                          </div>
                        )}
                        {errors.maxCapacity && (
                          <p className="mt-1 text-sm text-red-600">{errors.maxCapacity.message}</p>
                        )}
                      </div>
                    </div>

                    <div
                      id={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.PRICING : undefined}
                      className="md:col-span-2"
                    >
                      <CampLessonPricingFields
                        register={register}
                        errors={errors}
                        currencySymbol={currencySymbol}
                      />
                    </div>

                    <div
                      id={!editingCamp ? CAMP_CREATE_TOUR_STEP_IDS.RESOURCES : undefined}
                      className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6"
                    >
                    {/* Age Group */}
                    {academyAgeGroups.length > 0 ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Age Group
                        </label>
                        <select {...register('ageGroupId')} className="input">
                          <option value="">— None —</option>
                          {academyAgeGroups.map((ag) => (
                            <option key={ag.id} value={ag.id}>{ag.label}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Age Group
                        </label>
                        <select {...register('ageGroup')} className="input">
                          <option value="all">All Ages</option>
                          <option value="juniors">Juniors</option>
                          <option value="adults">Adults</option>
                        </select>
                      </div>
                    )}

                    {/* Location (Professional+) */}
                    {hasMultiLocation && locations.filter((l) => l.isActive).length > 0 && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Location
                        </label>
                        <select {...register('locationId')} className="input w-full">
                          <option value="">All locations / not specified</option>
                          {locations
                            .filter((l) => l.isActive)
                            .sort((a, b) => a.sortOrder - b.sortOrder)
                            .map((loc) => (
                              <option key={loc.id} value={loc.id}>
                                {loc.name}
                              </option>
                            ))}
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          When set, only courts at this location are shown below.
                        </p>
                      </div>
                    )}

                    {/* Courts */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Courts * ({selectedCourtIds.length} selected)
                      </label>
                      <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
                        {activeCourtsForPicker.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">No active courts available</p>
                        ) : (
                          activeCourtsForPicker.map((court) => (
                            <label key={court.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2">
                              <input
                                type="checkbox"
                                checked={selectedCourtIds.includes(court.id)}
                                onChange={() => toggleCourtSelection(court.id)}
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{court.name}</span>
                            </label>
                          ))
                        )}
                      </div>
                      {errors.courtIds && (
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.courtIds.message}</p>
                      )}
                    </div>

                    {/* Coaches */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Coaches * ({selectedCoachIds.length} selected)
                      </label>
                      <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-800">
                        {coaches.filter((c) => c.isActive).length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">No active coaches available</p>
                        ) : (
                          coaches.filter((c) => c.isActive).map((coach) => (
                            <label key={coach.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700 rounded px-2">
                              <input
                                type="checkbox"
                                checked={selectedCoachIds.includes(coach.id)}
                                onChange={() => toggleCoachSelection(coach.id)}
                                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {coach.firstName} {coach.lastName}
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      {errors.coachIds && (
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.coachIds.message}</p>
                      )}
                    </div>

                    {/* Description */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <textarea
                        {...register('description')}
                        rows={6}
                        className="input min-h-[10rem] w-full resize-y"
                        placeholder="Camp description, what to bring, schedule details..."
                      />
                    </div>

                    <div className="md:col-span-2">
                      <LessonCoverImageField
                        displayUrl={coverDisplayUrl}
                        uploading={uploadingCover}
                        disabled={submitting || uploadingCover}
                        inputId="camp-cover-upload"
                        onFileSelect={handleCoverFile}
                        onRemove={handleCoverRemove}
                      />
                      <input type="hidden" {...register('imageUrl')} />
                    </div>

                    {/* Active */}
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('isActive')}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Active (available for booking)
                        </span>
                      </label>
                    </div>

                    {/* Featured */}
                    <div className="md:col-span-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          {...register('isFeatured')}
                          className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Featured (show on public academy page)
                        </span>
                      </label>
                    </div>
                    </div>
                  </div>

                </form>
        </Drawer>

        {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : camps.length === 0 ? (
            <DashboardEmptyStateCard contentClassName="text-center sm:px-12">
              <DashboardEmptyStateIcon icon={Tent} />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No camps yet</h3>
              <CampsGuideOverview className="mx-auto max-w-2xl text-left" />
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" aria-label="Add new camp options">
                      <Plus className="h-4 w-4" aria-hidden />
                      Add new
                      <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="min-w-[12rem]">
                    <DropdownMenuItem onSelect={() => openNewCampForm()}>
                      <Tent className="h-4 w-4" aria-hidden />
                      Single camp
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate('/admin/camps/new')}>
                      <Layers className="h-4 w-4" aria-hidden />
                      Multiple camps
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button type="button" variant="outline" onClick={() => setShowImportModal(true)}>
                  <Upload className="h-4 w-4" aria-hidden />
                  Import camps
                </Button>
              </div>
            </DashboardEmptyStateCard>
          ) : (
            <div className="space-y-5">
              <div id={CAMP_MANAGEMENT_TOUR_STEP_IDS.LIST_TABS}>
              <DashboardTabNav<CampListTab>
                withMargin={false}
                tabs={[
                  {
                    id: 'current' as const,
                    label: 'Current & upcoming',
                    mobileLabel: 'Upcoming',
                    suffix: `(${currentCampsCount})`,
                  },
                  {
                    id: 'previous' as const,
                    label: 'Previous camps',
                    mobileLabel: 'Past',
                    suffix: `(${previousCampsCount})`,
                  },
                ]}
                activeTab={campListTab}
                onChange={setCampListTab}
                ariaLabel="Camp list filters"
              />
              </div>

              <div
                id={CAMP_MANAGEMENT_TOUR_STEP_IDS.FILTERS}
                className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
              >
                <div className="relative w-full min-w-0 sm:max-w-md">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    aria-hidden
                  />
                  <Input
                    type="search"
                    value={campSearchQuery}
                    onChange={(e) => setCampSearchQuery(e.target.value)}
                    placeholder="Search by name or description…"
                    className="h-9 w-full pl-9"
                    aria-label="Search camps"
                  />
                </div>
                <div className="flex w-full items-center justify-end gap-2 sm:w-auto sm:shrink-0">
                  {campScheduleView === 'day' && coaches.some((coach) => coach.isActive) ? (
                    <DayColumnAxisToggle value={dayColumnAxis} onChange={setDayColumnAxis} />
                  ) : null}
                  {hasActiveCampFilters ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 shrink-0 px-2.5 text-muted-foreground"
                      onClick={clearCampFilters}
                    >
                      Reset filters
                    </Button>
                  ) : null}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="relative h-9 w-9 shrink-0"
                        aria-label="Camp list actions"
                      >
                        <MoreVertical className="h-4 w-4" aria-hidden />
                        {hasActiveCampFilters ? (
                          <span
                            className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary"
                            aria-hidden
                          />
                        ) : null}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="min-w-[12rem]">
                      <DropdownMenuItem onClick={handleExportCamps}>
                        <Download className="h-4 w-4 mr-2" aria-hidden />
                        Export
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowImportModal(true)}>
                        <Upload className="h-4 w-4 mr-2" aria-hidden />
                        Import
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        value={campScheduleView}
                        onValueChange={(value) =>
                          setCampScheduleView(value as CampScheduleViewMode)
                        }
                      >
                        <DropdownMenuRadioItem value="list">
                          <List className="h-4 w-4 mr-2" aria-hidden />
                          List view
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="weekly">
                          <CalendarClock className="h-4 w-4 mr-2" aria-hidden />
                          Weekly view
                        </DropdownMenuRadioItem>
                        <DropdownMenuRadioItem value="day">
                          <CalendarDays className="h-4 w-4 mr-2" aria-hidden />
                          Day view
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                      {hasMultiLocation && activeLocations.length > 0 ? (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                              <MapPin className="h-4 w-4 mr-2" aria-hidden />
                              <span className="truncate">Location</span>
                              <span className="ml-auto pl-2 text-xs text-muted-foreground truncate max-w-[9rem]">
                                {campLocationFilterLabel}
                              </span>
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="min-w-[12rem]">
                              <DropdownMenuRadioGroup
                                value={campLocationFilter || '__all__'}
                                onValueChange={(value) =>
                                  setCampLocationFilter(value === '__all__' ? '' : value)
                                }
                              >
                                <DropdownMenuRadioItem value="__all__">
                                  All locations
                                </DropdownMenuRadioItem>
                                {activeLocations.map((loc) => (
                                  <DropdownMenuRadioItem key={loc.id} value={loc.id}>
                                    {loc.name}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </>
                      ) : null}
                      <DropdownMenuCheckboxItem
                        checked={campFeaturedOnly}
                        onCheckedChange={(checked) => setCampFeaturedOnly(checked === true)}
                        onSelect={(event) => event.preventDefault()}
                      >
                        <Star
                          className={`h-4 w-4 mr-2 ${campFeaturedOnly ? 'fill-current' : ''}`}
                          aria-hidden
                        />
                        Featured only
                      </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {sortedFilteredCamps.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-muted/20 px-6 py-14 text-center">
                  <p className="text-gray-600 dark:text-gray-400">
                    {campsForTab.length === 0
                      ? campListTab === 'previous'
                        ? 'No previous camps yet. Past camps appear here after their end date.'
                        : 'No current or upcoming camps. Create a camp or check the Previous tab.'
                      : 'No camps match your filters.'}
                  </p>
                  {campsForTab.length > 0 && filteredCamps.length === 0 && (
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-4"
                      onClick={clearCampFilters}
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              ) : campScheduleView === 'weekly' ? (
                <AcademyScheduleCalendar
                  hideHeader
                  weeklyColumnHeaders="weekday"
                  showCurrentTimeIndicator={false}
                  lessons={campScheduleLessons}
                  terms={terms}
                  overrides={overrides}
                  lessonSessions={lessonSessions}
                  makeupSessions={makeupSessions}
                  pendingScheduleBookings={schedulePendingBookings}
                  scheduleEnrollmentBookings={scheduleEnrollmentBookings}
                  removedFromSessionEnrollments={removedFromSessionEnrollments}
                  reportedAbsenceSessions={reportedAbsenceSessions}
                  courtNamesById={courtNamesById}
                  coaches={coaches}
                  viewMode="weekly"
                  scheduleView="weekly"
                  weekStart={campCalendarWeekStart}
                  onWeekStartChange={setCampCalendarWeekStart}
                  onLessonOccurrenceClick={(payload) => setCampSessionDetail(payload)}
                  loading={loading}
                />
              ) : campScheduleView === 'day' ? (
                <DayCourtScheduleView
                  dateNavigation="weekday"
                  selectedDayOfWeek={campSelectedDayOfWeek}
                  onDayOfWeekChange={setCampSelectedDayOfWeek}
                  date={campPlanningDate}
                  courts={activeCourts}
                  occurrences={campDayOccurrences}
                  academyId={currentAcademy?.id}
                  editable
                  showCurrentTimeIndicator={false}
                  columnAxis={dayColumnAxis}
                  coaches={coaches}
                  onOccurrenceClick={(occ) =>
                    setCampSessionDetail(scheduleOccurrenceToClickPayload(occ))
                  }
                  onOccurrenceReschedule={handleCampOccurrenceReschedule}
                />
              ) : (
                <DashboardDataCard>
                  <PaginatedTable<Lesson>
                    columns={campTableColumns}
                    data={paginatedCamps}
                    pagination={
                      {
                        page: campPage,
                        limit: campsPerPage,
                        total: sortedFilteredCamps.length,
                        totalPages: campTotalPages,
                      } satisfies PaginationState
                    }
                    emptyMessage="No camps found."
                    onPageChange={setCampPage}
                    onRowClick={handleEdit}
                    noWrapperPadding
                  />
                </DashboardDataCard>
              )}
            </div>
          )}

      <SessionRescheduleNotifyDialog
        open={!!pendingCampReschedule}
        lessonName={pendingCampReschedule?.occurrence.lessonName ?? 'Camp session'}
        requireReason={pendingCampRescheduleMeta?.requireReason ?? false}
        showNotifyOption={pendingCampRescheduleMeta?.timeChanged ?? true}
        loading={campRescheduleSaving}
        onCancel={() => setPendingCampReschedule(null)}
        onConfirm={(notifyGuardians, reason) => {
          if (pendingCampReschedule) {
            void commitCampReschedule(pendingCampReschedule, notifyGuardians, reason);
          }
        }}
      />

      <LessonSessionDetailModal
        isOpen={!!campSessionDetail && !!currentAcademy?.id}
        onClose={() => setCampSessionDetail(null)}
        occurrence={campSessionDetail}
        coaches={coaches}
        lessons={camps}
        terms={terms}
        academyId={currentAcademy?.id ?? ''}
        academySlug={currentAcademy?.slug}
        hasValidSubscription={hasValidSubscription}
        makeupLessonEnabled={currentAcademy?.makeupLessonEnabled ?? false}
        onEdit={() => {
          if (!campSessionDetail) return;
          const lesson = camps.find((c) => c.id === campSessionDetail.lesson.id);
          if (lesson) handleEdit(lesson);
          setCampSessionDetail(null);
        }}
        onRequestPostpone={() => {
          if (!campSessionDetail?.lesson) return;
          setCancelCampTarget(campSessionDetail.lesson);
          setCampSessionDetail(null);
        }}
        onAfterAction={async () => {
          await refreshCampsAndCalendar();
        }}
      />

      <CampInstanceStudentsDrawer
        isOpen={!!staffEnrollCamp && !!currentAcademy?.id}
        onClose={closeStaffEnrollDrawer}
        camp={staffEnrollCamp}
        academyId={currentAcademy?.id ?? ''}
        academySlug={currentAcademy?.slug}
        makeupLessonEnabled={currentAcademy?.makeupLessonEnabled === true}
        onChanged={refreshCampsAndCalendar}
      />

      <DeleteCampConfirmDialog
        open={!!deletingCamp}
        onOpenChange={(open) => !open && !deletingCampLoading && setDeletingCamp(null)}
        campName={deletingCamp?.name ?? ''}
        onConfirm={confirmDeleteCamp}
        loading={deletingCampLoading}
      />

      <CancelTermLessonModal
        isOpen={!!cancelCampTarget}
        lesson={cancelCampTarget}
        academyId={currentAcademy?.id ?? ''}
        isLoading={cancelCampSubmitting}
        variant="camp"
        makeupLessonEnabled={currentAcademy?.makeupLessonEnabled === true}
        onCancel={() => setCancelCampTarget(null)}
        onConfirm={handleCancelCampConfirm}
      />

      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportCamps}
        title="Import Camps"
        description="Upload a CSV file to import multiple camps for this academy"
        formatGuide={campsFormatGuide}
        importingLabel="Importing camps…"
        requiredColumns={[
          'name',
          'lessonType',
          'startDate',
          'daysOfWeek',
          'startTime',
          'endTime',
          'maxCapacity',
          'campDayPrice',
          'campFullPrice',
          'courtNames',
          'coachNames',
        ]}
        additionalRowError={(row) => {
          const hasName = !!(row.name || '').trim();
          if (!hasName) {
            return 'name is required';
          }
          return undefined;
        }}
      />
    </div>
  );
}

export default function CampManagement() {
  return <CampManagementContent />;
}
